/**
 * NeuanlageAnsicht – eigenstaendige Ansicht "Neue Betreuung aus Beschluss" (24.10.2026, ersetzt den Pruefblock in der Dokumentkarte).
 * Vier getrennte Abschnitte: 1. Person und Dubletten, 2. Betreuerwechsel und Gerichtsdaten, 3. Aufgabenbereiche und Einwilligungsvorbehalt,
 * 4. Original, Zusammenfassung und Bestaetigung. Aufgaben- und Fristvorschlaege anderer Vorgaenge gehoeren nicht hierher (bleiben in der
 * Eingangspruefung). Es wird nichts angelegt, bevor der Benutzer ausdruecklich bestaetigt; das Backend prueft alles erneut.
 */
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Link2, ExternalLink, AlertTriangle, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { betreuungAPI } from '@/api/betreuungAPI';
import { authAPI } from '@/api/authAPI';
import { holePreviewUrl } from '@/lib/downloadTicket';
import { DokumentVorschauInhalt } from '@/components/DocumentPreviewModal';
import {
  ABSCHNITTE, ART_TEXT, PERSON_FELDER, GERICHT_FELDER, KREISE, FEHLT_TEXT, STAERKE_TEXT, EVB_STATUS_TEXT, grundAnzeige, startWerte, quelle, starkeDubletten,
  fehlenderSchritt, baueBestaetigung, konfliktText, reihenfolgeText, zusammenfassung, holeVorschlag, kiStatusText, abdeckungText,
  BEGINN_UNBEKANNT_HINWEIS,
} from './neueBetreuung';

const QUELLE_TEXT = { dokument: 'aus Beschluss', benutzer: 'manuell', leer: 'offen' };
const QUELLE_FARBE = { dokument: 'text-green-700', benutzer: 'text-amber-700', leer: 'text-red-700' };

function BelegText({ vorschlag, k }) {
  const beleg = ((vorschlag.felder || {})[k] || {}).beleg;
  return beleg ? <span className="block text-slate-500 italic break-words" data-testid={`nb-beleg-${k}`}>Beleg: „{beleg}“</span> : null;
}

function Feld({ vorschlag, k, label, typ, werte, setWerte, hinweis }) {
  const q = quelle(vorschlag, k, werte[k]);
  return (
    <label className="block text-xs" data-testid={`nb-feld-${k}`}>
      <span className="text-slate-600">{label}</span> <span className={QUELLE_FARBE[q]}>({QUELLE_TEXT[q]})</span>
      <Input type={typ || 'text'} value={werte[k] || ''} onChange={(e) => setWerte({ ...werte, [k]: e.target.value })} className="h-8" />
      {q === 'dokument' && <BelegText vorschlag={vorschlag} k={k} />}
      {hinweis && <span className="block text-amber-800" data-testid={`nb-feldhinweis-${k}`}>{hinweis}</span>}
    </label>
  );
}

export default function NeuanlageAnsicht({ start, onFertig, onSchliessen }) {
  const [vorschlag, setVorschlag] = useState(start);
  const [werte, setWerte] = useState(() => startWerte(start));
  const [art, setArt] = useState(start.art !== 'unklar' ? start.art : '');
  const [abschnitt, setAbschnitt] = useState(0);
  const [bestehend, setBestehend] = useState(null);
  const [dublettenGeprueft, setDublettenGeprueft] = useState(false);
  const [trotzDublette, setTrotzDublette] = useState(false);
  const [bestaetigt, setBestaetigt] = useState(false);
  const [ersetzen, setErsetzen] = useState(false);
  const [laeuft, setLaeuft] = useState(false);
  const [sperre, setSperre] = useState(null);
  const [neuAusgewertet, setNeuAusgewertet] = useState(false);
  const [kiLaeuft, setKiLaeuft] = useState(false);
  // Beim Oeffnen einmal frisch laden (gleicher Vorschlag, aktuelle Dublettenpruefung). Hat das Backend einen Vorschlag aelterer Erkennung neu
  // ausgewertet, gelten dessen Werte -- solange der Benutzer noch nichts geaendert hat (sonst bleibt seine Eingabe, mit Hinweis).
  // Laeuft die Qwen-Auswertung noch (Hintergrund), zeigt die Ansicht den Bearbeitungsstatus und fragt nach; Absenden erst danach.
  useEffect(() => {
    let aus = false;
    holeVorschlag((id) => betreuungAPI.neueBetreuungVorschlag(id), start.dokument_id, { onLaeuft: () => setKiLaeuft(true), abgebrochen: () => aus }).then((v) => {
      setKiLaeuft(false);
      if (aus || !v || v.id !== start.id) return;
      setVorschlag((alt) => ({ ...alt, ...v }));
      if (JSON.stringify(v.felder || {}) !== JSON.stringify(start.felder || {})) {
        setNeuAusgewertet(true);
        setWerte((w) => (JSON.stringify(w) === JSON.stringify(startWerte(start)) ? startWerte(v) : w));
        setArt((a) => (a === (start.art !== 'unklar' ? start.art : '') ? (v.art !== 'unklar' ? v.art : '') : a));
      }
    }).catch((e) => { setKiLaeuft(false); if (!aus) toast.error(e.message); });
    return () => { aus = true; };
  }, [start]);

  const konflikt = vorschlag.zuordnung_konflikt || null;
  const zustand = { vorschlag, werte, art, bestehend, dublettenGeprueft, trotzDublette, bestaetigt, konflikt, ersetzen };
  const schritt = fehlenderSchritt(zustand);
  const dok = vorschlag.dokument || {};
  const felder = vorschlag.felder || {};
  const dubletten = vorschlag.dubletten || [];
  const zf = zusammenfassung(vorschlag, werte, art);

  const original = async () => {
    try {
      const url = await holePreviewUrl(`/api/betreuung/dokumente/${vorschlag.dokument_id}/download-ticket`, `/api/betreuung/dokumente/${vorschlag.dokument_id}/download`, authAPI.getAuthHeaders());
      window.open(url, '_blank', 'noopener');
    } catch (e) { toast.error(e.message); }
  };
  const senden = async () => {
    setLaeuft(true); setSperre(null);
    try {
      const r = await betreuungAPI.neueBetreuungBestaetigen(vorschlag.id, baueBestaetigung(zustand));
      toast.success(r.neu_angelegt ? 'Klient und Betreuung angelegt, Beschluss zugeordnet.' : 'Beschluss dem bestehenden Klienten zugeordnet.');
      (r.hinweise || []).forEach((h) => toast.info(h));
      onFertig();
    } catch (e) {
      if (e.status === 409 && e.daten && e.daten.dubletten) { setSperre(e.daten.dubletten); setAbschnitt(0); }
      toast.error(e.message);
    }
    setLaeuft(false);
  };
  const verwerfen = async () => {
    try { await betreuungAPI.neueBetreuungAblehnen(vorschlag.id); toast.success('Vorschlag verworfen.'); onFertig(); } catch (e) { toast.error(e.message); }
  };
  const keineNeuanlage = bestehend && (
    <div className="text-sm text-violet-900" data-testid="nb-keine-neuanlage">Keine Neuanlage: der Beschluss wird {bestehend.name} zugeordnet (Abschnitt 1).</div>
  );

  // Original-PDF rechts neben der Pruefmaske (28.10.2026): dieselbe Vorschau wie im Dokument-Modal (Ticket, inline), kein zweiter Viewer
  const vorschauDok = { id: vorschlag.dokument_id, titel: dok.titel };
  return (
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 items-start" data-testid="nb-layout">
    <div className="border-2 border-violet-300 rounded-lg bg-white p-4 space-y-4 text-sm" data-testid="nb-ansicht">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <div className="text-lg font-semibold text-violet-900">Neuanlage aus Beschluss</div>
          <div className="text-xs text-slate-500" data-testid="nb-dok-kopf">{[dok.titel || 'Dokument', dok.datum, ART_TEXT[vorschlag.art]].filter(Boolean).join(' · ')}</div>
        </div>
        <Button size="sm" variant="outline" onClick={onSchliessen} data-testid="nb-schliessen"><ArrowLeft size={14} className="mr-1" /> Zurück zur Eingangsprüfung</Button>
      </div>

      {kiLaeuft && <div className="text-xs text-violet-800 flex items-center gap-1" data-testid="nb-ki-laeuft"><Loader2 size={12} className="animate-spin" />
        Qwen wertet den Beschluss aus – die Werte werden danach aktualisiert. Bestätigen ist erst danach möglich.</div>}
      {neuAusgewertet && <div className="text-xs text-violet-800" data-testid="nb-neu-ausgewertet">Der Vorschlag wurde mit der aktuellen Beschluss-Erkennung neu ausgewertet.</div>}
      <ol className="flex flex-wrap gap-2" data-testid="nb-schritte">
        {ABSCHNITTE.map((t, i) => (
          <li key={t}>
            <button type="button" onClick={() => setAbschnitt(i)} data-testid={`nb-tab-${i + 1}`}
              className={`px-3 py-1 rounded-full border text-xs ${abschnitt === i ? 'bg-violet-600 text-white border-violet-600' : 'bg-white text-slate-700'}`}>{i + 1}. {t}</button>
          </li>
        ))}
      </ol>

      {abschnitt === 0 && (
        <section className="space-y-3" data-testid="nb-abschnitt-1">
          {konflikt && (
            <div className="border border-red-300 bg-red-50 rounded p-2 text-red-900" data-testid="nb-konflikt-ansicht"><AlertTriangle size={14} className="inline mr-1" />
              <strong>Zuordnung widersprüchlich:</strong> {konfliktText(konflikt)} Die Zuordnung wurde nicht verändert. Vorschläge des bisher zugeordneten Klienten werden hier nicht übernommen.
              <label className="flex items-start gap-2 mt-1"><input type="checkbox" checked={ersetzen} onChange={(e) => setErsetzen(e.target.checked)} className="mt-1" data-testid="nb-ersetzen" />
                <span>Die bestehende Zuordnung ({konflikt.klient_name || konflikt.klient_id}) ist falsch und wird ersetzt.</span></label>
            </div>
          )}
          {reihenfolgeText(vorschlag) && (
            <div className={felder.namensreihenfolge && felder.namensreihenfolge.gegengeprueft ? 'text-xs text-green-800' : 'text-xs text-amber-800'} data-testid="nb-reihenfolge">
              {reihenfolgeText(vorschlag)}</div>
          )}
          {!bestehend && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="nb-felder-person">
              {PERSON_FELDER.map(([k, label, typ]) => <Feld key={k} vorschlag={vorschlag} k={k} label={label} typ={typ} werte={werte} setWerte={setWerte} />)}
            </div>
          )}
          <div className="space-y-1" data-testid="nb-dubletten">
            <div className="font-medium">Dublettenprüfung</div>
            {dubletten.length === 0 && <div className="text-green-800">Kein vorhandener Klient passt zu Name, Geburtsdatum oder Aktenzeichen.</div>}
            {dubletten.map((d) => (
              <div key={d.klient_id} className={`border rounded p-2 bg-white ${(sperre || []).some((s) => s.klient_id === d.klient_id) ? 'border-red-400' : ''}`}>
                <Badge variant="outline" className={d.staerke === 'moeglich' ? '' : 'bg-red-50 text-red-800 border-red-300'}>{STAERKE_TEXT[d.staerke]}</Badge>{' '}
                <strong>{d.name}</strong> {[d.geburtsdatum, d.aktenzeichen, d.status].filter(Boolean).join(' · ')}
                <div className="text-xs text-slate-600">{d.gruende.map(grundAnzeige).join(', ')}</div>
                <button type="button" className="text-violet-700 hover:underline text-xs" onClick={() => setBestehend(bestehend && bestehend.klient_id === d.klient_id ? null : d)} data-testid="nb-bestehend">
                  <Link2 size={12} className="inline mr-1" />{bestehend && bestehend.klient_id === d.klient_id ? 'Auswahl aufheben' : 'Beschluss diesem Klienten zuordnen (keine Neuanlage)'}
                </button>
              </div>
            ))}
            <label className="flex items-start gap-2"><input type="checkbox" checked={dublettenGeprueft} onChange={(e) => setDublettenGeprueft(e.target.checked)} className="mt-1" data-testid="nb-dubletten-geprueft" />
              <span>Ich habe die Dublettenliste geprüft.</span></label>
            {!bestehend && starkeDubletten(vorschlag).length > 0 && (
              <label className="flex items-start gap-2 text-red-800"><input type="checkbox" checked={trotzDublette} onChange={(e) => setTrotzDublette(e.target.checked)} className="mt-1" data-testid="nb-trotz" />
                <span>Es handelt sich trotz möglicher Dublette um eine andere Person – trotzdem neu anlegen.</span></label>
            )}
          </div>
        </section>
      )}

      {abschnitt === 1 && (
        <section className="space-y-3" data-testid="nb-abschnitt-2">
          {keineNeuanlage || (
            <>
              <div className="flex flex-wrap gap-3 items-center" data-testid="nb-art">
                <Badge variant="outline">Erkannt: {ART_TEXT[vorschlag.art]}</Badge>
                {['betreuerwechsel', 'erstbestellung'].map((a) => (
                  <label key={a} className="flex items-center gap-1 cursor-pointer">
                    <input type="radio" name={`nb-art-${vorschlag.id}`} checked={art === a} onChange={() => setArt(a)} /> {ART_TEXT[a]}
                  </label>
                ))}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="nb-felder-gericht">
                {GERICHT_FELDER.map(([k, label, typ]) => <Feld key={k} vorschlag={vorschlag} k={k} label={label} typ={typ} werte={werte} setWerte={setWerte} />)}
                {art === 'betreuerwechsel' && (
                  <>
                    <Feld vorschlag={vorschlag} k="bisheriger_betreuer" label="Bisherige/r Betreuer/in" werte={werte} setWerte={setWerte} />
                    {felder.neuer_betreuer && (
                      <div className="text-xs" data-testid="nb-neuer-betreuer"><span className="text-slate-600">Neu bestellt (laut Beschluss)</span>
                        <div className="font-medium">{felder.neuer_betreuer.wert}</div><BelegText vorschlag={vorschlag} k="neuer_betreuer" /></div>
                    )}
                    <Feld vorschlag={vorschlag} k="betreuung_ursprung_beginn" label="Beginn der Betreuung insgesamt (VBVG, historisch)" typ="date" werte={werte} setWerte={setWerte}
                      hinweis={werte.betreuung_ursprung_beginn
                        ? 'Muss vor der aktuellen Bestellung liegen – das Wechsel- bzw. Wirksamkeitsdatum ist kein historischer Beginn.'
                        : `${BEGINN_UNBEKANNT_HINWEIS} Status: noch zu klären – die Neuanlage ist trotzdem möglich; Nachtrag später in der Akte.`} />
                  </>
                )}
              </div>
            </>
          )}
        </section>
      )}

      {abschnitt === 2 && (
        <section className="space-y-3" data-testid="nb-abschnitt-3">
          {keineNeuanlage || (
            <>
              {/* 29.10.2026: Gerichtswortlaut ist FUEHREND -- je Aufgabenbereich eine bearbeitbare Zeile; Kategorien darunter nur als zusaetzliche Einordnung */}
              <div className="space-y-1" data-testid="nb-wortlaut">
                <div className="font-medium">Aufgabenbereiche laut Beschluss (Wortlaut, maßgeblich){' '}
                  <span className={`text-xs ${QUELLE_FARBE[quelle(vorschlag, 'aufgabenkreise_wortlaut', werte.aufgabenkreise_wortlaut)]}`} data-testid="nb-wortlaut-quelle">
                    ({QUELLE_TEXT[quelle(vorschlag, 'aufgabenkreise_wortlaut', werte.aufgabenkreise_wortlaut)]})</span></div>
                <div className="text-xs text-slate-500">Jeder Aufgabenbereich einzeln und wortgetreu wie im Beschluss – keine Zusammenfassung. Korrekturen nur nach Abgleich mit dem Original rechts.</div>
                {(werte.aufgabenkreise_wortlaut || []).map((z, i) => (
                  <div key={i} className="flex gap-1 items-center">
                    <span className="text-xs text-slate-500 w-5 text-right">{i + 1}.</span>
                    <Input value={z} className="h-8" data-testid={`nb-wortlaut-${i}`}
                      onChange={(e) => setWerte({ ...werte, aufgabenkreise_wortlaut: werte.aufgabenkreise_wortlaut.map((x, j) => (j === i ? e.target.value : x)) })} />
                    <Button size="sm" variant="ghost" data-testid={`nb-wortlaut-entfernen-${i}`}
                      onClick={() => setWerte({ ...werte, aufgabenkreise_wortlaut: werte.aufgabenkreise_wortlaut.filter((_, j) => j !== i) })}>Entfernen</Button>
                  </div>
                ))}
                <Button size="sm" variant="outline" data-testid="nb-wortlaut-neu"
                  onClick={() => setWerte({ ...werte, aufgabenkreise_wortlaut: [...(werte.aufgabenkreise_wortlaut || []), ''] })}>Aufgabenbereich hinzufügen</Button>
                {quelle(vorschlag, 'aufgabenkreise_wortlaut', werte.aufgabenkreise_wortlaut) === 'dokument' && <BelegText vorschlag={vorschlag} k="aufgabenkreise_wortlaut" />}
              </div>
              <div data-testid="nb-kreise">
                <span className="text-xs text-slate-600">Interne Kategorien – nur zusätzliche Einordnung, ändert den Wortlaut nicht <span className={QUELLE_FARBE[quelle(vorschlag, 'aufgabenkreise', werte.aufgabenkreise)]}>({QUELLE_TEXT[quelle(vorschlag, 'aufgabenkreise', werte.aufgabenkreise)]})</span></span>
                <div className="flex flex-wrap gap-3">
                  {KREISE.map(([k, label]) => (
                    <label key={k} className="flex items-center gap-1 text-xs">
                      <input type="checkbox" checked={(werte.aufgabenkreise || []).includes(k)} data-testid={`nb-kreis-${k}`}
                        onChange={(e) => setWerte({ ...werte, aufgabenkreise: e.target.checked ? [...(werte.aufgabenkreise || []), k] : (werte.aufgabenkreise || []).filter((x) => x !== k) })} /> {label}
                    </label>
                  ))}
                </div>
              </div>
              <label className="block text-xs" data-testid="nb-evb">
                Einwilligungsvorbehalt <span className={QUELLE_FARBE[quelle(vorschlag, 'einwilligungsvorbehalt', werte.einwilligungsvorbehalt)]}>({QUELLE_TEXT[quelle(vorschlag, 'einwilligungsvorbehalt', werte.einwilligungsvorbehalt)]})</span>
                <select className="border rounded px-1 py-1 ml-2" value={werte.einwilligungsvorbehalt === null || werte.einwilligungsvorbehalt === undefined ? '' : String(werte.einwilligungsvorbehalt)}
                  onChange={(e) => setWerte({ ...werte, einwilligungsvorbehalt: e.target.value === '' ? null : e.target.value === 'true' })} data-testid="nb-evb-wahl">
                  <option value="">offen (nicht belegt)</option><option value="false">nicht angeordnet</option><option value="true">besteht / angeordnet</option>
                </select>
                {felder.einwilligungsvorbehalt && felder.einwilligungsvorbehalt.status && quelle(vorschlag, 'einwilligungsvorbehalt', werte.einwilligungsvorbehalt) === 'dokument' && (
                  <span className="block text-green-800" data-testid="nb-evb-status">Laut Beschluss: {EVB_STATUS_TEXT[felder.einwilligungsvorbehalt.status] || felder.einwilligungsvorbehalt.status}</span>
                )}
                {quelle(vorschlag, 'einwilligungsvorbehalt', werte.einwilligungsvorbehalt) === 'dokument' && <BelegText vorschlag={vorschlag} k="einwilligungsvorbehalt" />}
              </label>
            </>
          )}
        </section>
      )}

      {abschnitt === 3 && (
        <section className="space-y-3" data-testid="nb-abschnitt-4">
          <div className="flex flex-wrap gap-2 items-center">
            <Button size="sm" variant="outline" onClick={original} data-testid="nb-original"><ExternalLink size={14} className="mr-1" /> Originaldokument öffnen</Button>
          </div>
          {kiStatusText(vorschlag) && <p className="text-xs text-slate-500" data-testid="nb-ki-status">Auswertung: {kiStatusText(vorschlag)}</p>}
          {abdeckungText(vorschlag) && <p className="text-xs text-slate-500" data-testid="nb-ki-abdeckung">{abdeckungText(vorschlag)}</p>}
          {dok.zusammenfassung && <p className="text-slate-600" data-testid="nb-dok-zusammenfassung">{dok.zusammenfassung}</p>}
          {!bestehend ? (
            <table className="w-full text-xs" data-testid="nb-zusammenfassung"><tbody>
              {zf.map((z) => (
                <tr key={z.k} className="border-b" data-testid={`nb-zf-${z.k}`} data-quelle={z.quelle}>
                  <td className="py-1 pr-2 text-slate-600 w-1/3">{z.label}</td><td className="py-1 pr-2">{z.wert || '—'}</td><td className={`py-1 ${QUELLE_FARBE[z.quelle]}`}>{QUELLE_TEXT[z.quelle]}</td>
                </tr>))}
            </tbody></table>
          ) : keineNeuanlage}
          {(vorschlag.fehlend || []).length > 0 && !bestehend && (
            <div className="text-xs text-red-800" data-testid="nb-fehlend">Im Beschluss nicht belegt – bleibt ausdrücklich offen, wenn leer: {vorschlag.fehlend.map((k) => FEHLT_TEXT[k] || k).join(', ')}</div>
          )}
          {(vorschlag.hinweise || []).length > 0 && <ul className="list-disc pl-5 text-amber-800 text-xs" data-testid="nb-hinweise">{vorschlag.hinweise.map((h) => <li key={h}>{h}</li>)}</ul>}
          <div className="text-xs text-slate-500" data-testid="nb-trennung">Aufgaben- und Fristvorschläge zu diesem Dokument werden hier nicht übernommen; sie bleiben in der Eingangsprüfung.</div>
          <label className="flex items-start gap-2"><input type="checkbox" checked={bestaetigt} onChange={(e) => setBestaetigt(e.target.checked)} className="mt-1" data-testid="nb-bestaetigt" />
            <span>{bestehend ? `Beschluss ${bestehend.name} zuordnen.` : 'Ich bestätige die Neuanlage von Klient und Betreuung mit den obigen Angaben.'}</span></label>
          {schritt && <div className="text-xs text-slate-600" data-testid="nb-schritt">{schritt}</div>}
          <div className="flex gap-2">
            <Button size="sm" onClick={senden} disabled={laeuft || kiLaeuft || !!schritt} data-testid="nb-senden">
              {laeuft ? <Loader2 className="animate-spin mr-1" size={14} /> : null}{bestehend ? 'Zuordnen' : 'Klient + Betreuung anlegen'}
            </Button>
            <Button size="sm" variant="outline" onClick={verwerfen} disabled={laeuft} data-testid="nb-verwerfen">Vorschlag verwerfen</Button>
          </div>
        </section>
      )}

      <div className="flex justify-between">
        <Button size="sm" variant="outline" onClick={() => setAbschnitt(abschnitt - 1)} disabled={abschnitt === 0} data-testid="nb-zurueck">Zurück</Button>
        {abschnitt < 3 && <Button size="sm" onClick={() => setAbschnitt(abschnitt + 1)} data-testid="nb-weiter">Weiter: {ABSCHNITTE[abschnitt + 1]}</Button>}
      </div>
    </div>
    <aside className="border rounded-lg bg-white p-1 xl:sticky xl:top-2 h-[85vh] flex flex-col" data-testid="nb-original-vorschau">
      <div className="text-xs text-slate-500 px-2 py-1 truncate">Original: {dok.titel || 'Dokument'}</div>
      <div className="flex-1 min-h-0"><DokumentVorschauInhalt dokument={vorschauDok} hoehe="min-h-[60vh]" /></div>
    </aside>
    </div>
  );
}
